import { apiClient } from "./client";

export interface BenchmarkBandDescriptor {
  descriptor: string;
  evidence_rules: string[];
  disallowed: string[];
}

export interface BenchmarkSubskill {
  description: string;
  weight: number;
  bands: {
    emerging: BenchmarkBandDescriptor;
    expected: BenchmarkBandDescriptor;
    exceeding: BenchmarkBandDescriptor;
  };
}

export interface BenchmarkStrand {
  description: string;
  subskills: Record<string, BenchmarkSubskill>;
}

export interface BenchmarkScoringLogic {
  minimum_evidence_clips: number;
  maximum_evidence_clips: number;
  confidence_threshold: number;
  strand_weights: Record<string, number>;
}

export interface BenchmarkFeedbackTemplates {
  strengths: Record<string, string[]>;
  next_steps: Record<string, string[]>;
}

export interface BenchmarkEALScaffolds {
  sentence_stems: string[];
  planning_frames: string[];
  vocabulary_supports: string[];
}

export interface BenchmarkDefinition {
  version: string;
  age_band: string;
  mode: string;
  strands: Record<string, BenchmarkStrand>;
  scoring_logic: BenchmarkScoringLogic;
  feedback_templates?: BenchmarkFeedbackTemplates;
  eal_scaffolds?: BenchmarkEALScaffolds;
}

export interface BenchmarkVersion {
  id: string;
  version: string;
  age_band: string;
  mode: string;
  definition: BenchmarkDefinition;
  is_active: boolean;
  isDeprecated: boolean;
  deprecated_at: string | null;
  replacement_version: string;
  created_at: string;
  notes: string;
}

export interface BenchmarkOptions {
  age_bands: string[];
  modes: { value: string; label: string }[];
  strands: { value: string; label: string }[];
  bands: { value: string; label: string }[];
}

export const benchmarkApi = {
  list: async (params?: {
    age_band?: string;
    mode?: string;
    is_active?: boolean;
  }): Promise<{ results: BenchmarkVersion[]; count: number }> => {
    const response = await apiClient.get("/benchmarks/", { params });
    return response.data;
  },

  get: async (id: string): Promise<BenchmarkVersion> => {
    const response = await apiClient.get(`/benchmarks/${id}/`);
    return response.data;
  },

  create: async (data: Partial<BenchmarkVersion>): Promise<BenchmarkVersion> => {
    const response = await apiClient.post("/benchmarks/", data);
    return response.data;
  },

  update: async (id: string, data: Partial<BenchmarkVersion>): Promise<BenchmarkVersion> => {
    const response = await apiClient.patch(`/benchmarks/${id}/`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/benchmarks/${id}/`);
  },

  activate: async (id: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/benchmarks/${id}/activate/`);
    return response.data;
  },

  deprecate: async (id: string, replacement_version?: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/benchmarks/${id}/deprecate/`, {
      replacement_version,
    });
    return response.data;
  },

  options: async (): Promise<BenchmarkOptions> => {
    const response = await apiClient.get("/benchmarks/options/");
    return response.data;
  },
};